import React, { useMemo } from 'react';

import { useKitTranslate } from 'common/components/kit-shared/useKitTranslate';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import { useKitView } from 'containers/KitViewContext';
import { buildSpecLines, assemblyQty } from 'common/components/kit-shared/assemblySummary';
import AssemblyName from 'common/components/kit-shared/AssemblyName';
import type { IAssembly, IKit, IKitAccessory } from 'containers/KitWorkspaceStorage/types';

import Button from 'common/components/Button';
import { ButtonSchema, ButtonSize } from 'common/components/Button/constants';
import BottomSheet from 'common/components/kit-shared/BottomSheet';
import styles from './MyAssembliesPopup.module.scss';

interface IMyAssembliesPopupProps {
  onClose: () => void;
}

// Read-only review card: assembly name + spec detail rows (matches the pre-kit
// "My Assemblies" look). No row actions — editing/adding lives in Manage Kits.
const ReviewCard = ({ assembly }: { assembly: IAssembly }) => {
  const t = useKitTranslate();
  const specLines = buildSpecLines(assembly);
  const qty = assemblyQty(assembly);

  return (
    <div className={styles.card}>
      <div className={styles.cardTitle}>
        <AssemblyName assembly={assembly} />
        {qty > 1 && <span className={styles.qtyBadge}>{`×${qty}`}</span>}
      </div>
      {specLines.map((line) => (
        <p key={line.label} className={styles.row}>
          <strong>
            {line.label}
            :
          </strong>
          {' '}
          {line.value}
        </p>
      ))}
      {assembly.notes && (
        <p className={styles.row}>
          <strong>
            {t('texts.kits.assemblyNotesPlaceholder')}
            :
          </strong>
          {' '}
          {assembly.notes}
        </p>
      )}
    </div>
  );
};

// Loose parts (kit- or standalone-level companions) listed under their group.
const LooseParts = ({ accessories }: { accessories: IKitAccessory[] }) => {
  const t = useKitTranslate();
  if (accessories.length === 0) return null;

  return (
    <div className={styles.loose}>
      <div className={styles.looseHead}>{t('texts.kits.partsHeader')}</div>
      {accessories.map((acc) => (
        <p key={acc.uuid} className={styles.looseRow}>
          <strong>{acc.partNumber}</strong>
          {acc.description ? ` — ${acc.description}` : ''}
          {` ×${acc.qty}`}
        </p>
      ))}
    </div>
  );
};

// One kit rendered as a kit: name header, its assemblies, and its loose parts.
const KitSection = ({ kit }: { kit: IKit }) => {
  const t = useKitTranslate();
  const name = kit.name?.trim() || t('texts.kits.pills.untitled');
  const assemblies = [...kit.lines]
    .sort((a, b) => a.position - b.position)
    .map((line) => line.assembly)
    .filter((a): a is IAssembly => Boolean(a));

  return (
    <div className={styles.group}>
      <div className={styles.groupHead}>{name}</div>
      {assemblies.map((a) => <ReviewCard key={a.uuid} assembly={a} />)}
      <LooseParts accessories={kit.accessories} />
    </div>
  );
};

/**
 * First-touch "My assemblies" popup: a read-only review of saved standalone
 * assemblies (the pre-kit My Assemblies look), plus a "Manage Kits" button that
 * opens the full overlay where assemblies are added to kits / edited / deleted.
 * Bottom-sheet on mobile.
 */
const MyAssembliesPopup = ({ onClose }: IMyAssembliesPopupProps) => {
  const t = useKitTranslate();
  const { standalone, kits } = useKitWorkspace();
  const { openManageKits } = useKitView();

  // Show EVERY assembly in the workspace, not just the standalone ones — those
  // already added to a kit must appear here too. Deduped by uuid (an assembly is
  // in at most one kit, so this just guards against null/orphan lines). Ordered
  // as a single chronological list by creation order (position, oldest first),
  // regardless of whether an assembly is standalone or grouped into a kit.
  const allAssemblies = useMemo<IAssembly[]>(() => {
    const byUuid = new Map<string, IAssembly>();
    standalone.forEach((a) => { if (a) byUuid.set(a.uuid, a); });
    kits.forEach((kit) => kit.lines.forEach((line) => {
      if (line.assembly) byUuid.set(line.assembly.uuid, line.assembly);
    }));
    return Array.from(byUuid.values()).sort((a, b) => a.position - b.position);
  }, [standalone, kits]);

  // When kits exist, show them AS kits (each with its assemblies + loose parts)
  // and list the ungrouped ones under a "Single assemblies" header. With no kits,
  // fall back to the flat chronological list above.
  const hasKits = kits.length > 0;
  const sortedKits = useMemo(() => [...kits].sort((a, b) => a.position - b.position), [kits]);
  const sortedStandalone = useMemo(
    () => standalone.filter(Boolean).sort((a, b) => a.position - b.position),
    [standalone],
  );

  const goToManageKits = () => {
    onClose();
    openManageKits();
  };

  return (
    <BottomSheet
      title={t('texts.kits.assemblies.title', { count: allAssemblies.length })}
      subtitle={t('texts.kits.assemblies.subtitle')}
      onClose={onClose}
      footer={(
        <div className={styles.footerActions}>
          <Button schema={ButtonSchema.secondary} size={ButtonSize.small} onClick={onClose}>
            {t('texts.kits.assemblies.close')}
          </Button>
          <Button schema={ButtonSchema.primary} size={ButtonSize.small} onClick={goToManageKits}>
            {t('texts.kits.manageKits')}
          </Button>
        </div>
      )}
    >
      {allAssemblies.length === 0 && (
        <p className={styles.empty}>{t('texts.kits.assemblies.empty')}</p>
      )}
      {allAssemblies.length > 0 && hasKits && (
        <div className={styles.list}>
          {sortedKits.map((kit) => <KitSection key={kit.uuid} kit={kit} />)}
          {sortedStandalone.length > 0 && (
            <div className={styles.group}>
              <div className={styles.groupHead}>{t('texts.kits.assemblies.single')}</div>
              {sortedStandalone.map((a) => <ReviewCard key={a.uuid} assembly={a} />)}
            </div>
          )}
        </div>
      )}
      {allAssemblies.length > 0 && !hasKits && (
        <div className={styles.list}>
          {allAssemblies.map((a) => <ReviewCard key={a.uuid} assembly={a} />)}
        </div>
      )}
    </BottomSheet>
  );
};

export default MyAssembliesPopup;
